import {
  approvedRefs,
  statusDisplay,
  summariseApplyResults,
} from "./ConfigStaging.support";
import {
  STATUS_FILTERS,
  approvedSummaries,
  buildRows,
  describeApplyResults,
  statusQueryParam,
  uniqueValues,
} from "./ConfigStagingPage.support";
import StagedChangeDetail from "./StagedChangeDetail";
import { OpenInNewRounded as OpenInNewIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { readableDate } from "common/dates";
import Component from "components/Component";
import AdminDashboard from "components/admin/AdminDashboard";
import BigLoader from "components/common/BigLoader";
import Button from "components/common/Button";
import IconButton from "components/common/IconButton";
import DashboardContent from "components/layout/DashboardContent";
import { useMemo, useState } from "react";
import {
  useApplyStagedConfigMutation,
  useListStagedConfigQuery,
} from "services/adminService";

const ApplyConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  onReview,
  summaries,
  isApplying,
  error,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth={"sm"} fullWidth>
    <DialogTitle>{"Apply approved changes"}</DialogTitle>
    <DialogContent>
      <Stack gap={1.5}>
        <Typography variant={"body2"}>
          {`The following ${summaries.length} approved change${
            summaries.length === 1 ? "" : "s"
          } will be applied to live config:`}
        </Typography>
        {summaries.map((summary) => (
          <Paper
            key={`${summary.companyId}/${summary.id}`}
            variant={"outlined"}
            sx={{ p: 1.5 }}
          >
            <Stack
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              gap={1}
            >
              <Stack gap={0.5} minWidth={0}>
                <Stack
                  direction={"row"}
                  gap={1}
                  alignItems={"center"}
                  flexWrap={"wrap"}
                >
                  <Typography variant={"subtitle2"}>{summary.title}</Typography>
                  {(summary.prNumbers ?? []).map((prNumber) => (
                    <Chip
                      key={prNumber}
                      label={`#${prNumber}`}
                      size={"small"}
                    />
                  ))}
                  {summary.stale && (
                    <Chip
                      label={"Updated config"}
                      color={"warning"}
                      size={"small"}
                    />
                  )}
                </Stack>
                <Typography variant={"caption"} color={"text.secondary"} noWrap>
                  {`${summary.key} — ${summary.companyId}`}
                </Typography>
              </Stack>
              <IconButton
                icon={OpenInNewIcon}
                scale={"small"}
                tooltip={"Review this change"}
                onClick={() => onReview(summary)}
              />
            </Stack>
          </Paper>
        ))}
        {error && <Alert severity={"error"}>{error}</Alert>}
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onConfirm} isLoading={isApplying}>
        {"Apply"}
      </Button>
      <Button onClick={onClose} isDisabled={isApplying} color={"secondary"}>
        {"Cancel"}
      </Button>
    </DialogActions>
  </Dialog>
);

const OUTCOME_DISPLAY = {
  applied: { label: "Applied", color: "success" },
  failed: { label: "Failed", color: "error" },
  skipped: { label: "Skipped", color: "default" },
};

const ApplyResultsDialog = ({ results, onClose }) => (
  <Dialog open={Boolean(results)} onClose={onClose} maxWidth={"sm"} fullWidth>
    <DialogTitle>{"Apply results"}</DialogTitle>
    <DialogContent>
      {results && (
        <Stack gap={1.5}>
          <Stack direction={"row"} gap={1}>
            <Chip
              label={`${results.counts.applied} applied`}
              color={"success"}
              size={"small"}
            />
            <Chip
              label={`${results.counts.failed} failed`}
              color={results.counts.failed > 0 ? "error" : "default"}
              size={"small"}
            />
            <Chip
              label={`${results.counts.skipped} skipped`}
              color={"default"}
              size={"small"}
            />
          </Stack>
          {results.items.map((item) => {
            const display =
              OUTCOME_DISPLAY[item.outcome] ?? OUTCOME_DISPLAY.skipped;
            return (
              <Paper
                key={`${item.companyId}/${item.id}`}
                variant={"outlined"}
                sx={{ p: 1.5 }}
              >
                <Stack gap={0.5}>
                  <Stack direction={"row"} gap={1} alignItems={"center"}>
                    <Chip
                      label={display.label}
                      color={display.color}
                      size={"small"}
                    />
                    <Typography variant={"subtitle2"}>
                      {item.title ?? item.key ?? item.id}
                    </Typography>
                  </Stack>
                  <Typography variant={"caption"} color={"text.secondary"}>
                    {`${item.key ?? "unknown key"} — ${item.companyId}`}
                  </Typography>
                  {item.failureReason && (
                    <Typography variant={"body2"} color={"error"}>
                      {item.failureReason}
                    </Typography>
                  )}
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>{"Close"}</Button>
    </DialogActions>
  </Dialog>
);

const STAGED_STATUS_OPTIONS = [
  "staged",
  "approved",
  "rejected",
  "applied",
  "failed",
].map((status) => ({ value: status, label: statusDisplay(status).label }));

const makeColumns = (valueOptions) => [
  { field: "title", headerName: "Title", flex: 1.5, minWidth: 180 },
  {
    field: "key",
    headerName: "Config Key",
    flex: 1,
    minWidth: 140,
    type: "singleSelect",
    valueOptions: valueOptions.keys,
  },
  {
    field: "companyId",
    headerName: "Company",
    flex: 1,
    minWidth: 140,
    type: "singleSelect",
    valueOptions: valueOptions.companyIds,
  },
  {
    field: "prNumbers",
    headerName: "PRs",
    width: 130,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <Stack direction={"row"} gap={0.5} flexWrap={"wrap"}>
        {(params.value ?? []).map((prNumber) => (
          <Chip key={prNumber} label={`#${prNumber}`} size={"small"} />
        ))}
      </Stack>
    ),
  },
  {
    field: "githubLink",
    headerName: "Link",
    width: 70,
    sortable: false,
    filterable: false,
    renderCell: (params) =>
      params.value ? (
        <IconButton
          icon={OpenInNewIcon}
          scale={"small"}
          tooltip={"Open on GitHub"}
          onClick={(event) => {
            event.stopPropagation();
            window.open(params.value, "_blank", "noopener,noreferrer");
          }}
        />
      ) : null,
  },
  {
    field: "stagedBy",
    headerName: "Staged By",
    flex: 1,
    minWidth: 170,
    type: "singleSelect",
    valueOptions: valueOptions.stagedBy,
  },
  {
    field: "stagedAt",
    headerName: "Staged At",
    width: 170,
    renderCell: (params) => readableDate(params.value, { includeTime: true }),
  },
  {
    field: "lastActionBy",
    headerName: "Last Action",
    flex: 1.2,
    minWidth: 210,
    type: "singleSelect",
    valueOptions: valueOptions.lastActionBy,
    renderCell: (params) =>
      params.row.lastActionLabel ? (
        <Stack gap={0} justifyContent={"center"} height={"100%"}>
          <Typography variant={"body2"} noWrap>
            {`${params.row.lastActionLabel} by ${params.value}`}
          </Typography>
          <Typography variant={"caption"} color={"text.secondary"} noWrap>
            {readableDate(params.row.lastActionAt, { includeTime: true })}
          </Typography>
        </Stack>
      ) : null,
  },
  {
    field: "status",
    headerName: "Status",
    width: 110,
    type: "singleSelect",
    valueOptions: STAGED_STATUS_OPTIONS,
    renderCell: (params) => {
      const display = statusDisplay(params.value);
      return (
        <Chip label={display.label} color={display.color} size={"small"} />
      );
    },
  },
  {
    field: "stale",
    headerName: "Base",
    width: 150,
    type: "boolean",
    sortable: false,
    renderCell: (params) =>
      params.value ? (
        <Chip label={"Updated config"} color={"warning"} size={"small"} />
      ) : null,
  },
];

const ConfigStagingPage = ({ companyId = null }) => {
  const [filter, setFilter] = useState("pending");
  const [detailRef, setDetailRef] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applyError, setApplyError] = useState(null);
  const [applyResults, setApplyResults] = useState(null);

  const { data: records, isLoading, isError } = useListStagedConfigQuery({
    status: statusQueryParam(filter),
    companyId,
  });

  const [
    applyStagedConfig,
    { isLoading: isApplying },
  ] = useApplyStagedConfigMutation();

  const rows = useMemo(() => buildRows(records), [records]);
  const gridColumns = useMemo(() => {
    const cols = makeColumns({
      keys: uniqueValues(rows, "key"),
      companyIds: uniqueValues(rows, "companyId"),
      stagedBy: uniqueValues(rows, "stagedBy"),
      lastActionBy: uniqueValues(rows, "lastActionBy"),
    });
    return companyId
      ? cols.filter((column) => column.field !== "companyId")
      : cols;
  }, [rows, companyId]);
  const refs = approvedRefs(records ?? []);
  const summaries = approvedSummaries(records ?? []);

  const handleApply = async () => {
    setApplyError(null);
    try {
      const response = await applyStagedConfig({ changes: refs }).unwrap();
      setConfirmOpen(false);
      setApplyResults({
        counts: summariseApplyResults(response?.results ?? []),
        items: describeApplyResults(response?.results ?? [], records ?? []),
      });
    } catch (error) {
      setApplyError(error?.data?.message || error?.message || "Apply failed");
    }
  };

  return (
    <AdminDashboard>
      <DashboardContent isFullWidth={true}>
        <Component>
          <Stack direction={"row"} gap={1.5} alignItems={"center"}>
            <ToggleButtonGroup
              value={filter}
              onChange={(_, value) => {
                if (value) setFilter(value);
              }}
              exclusive
              size={"small"}
            >
              {STATUS_FILTERS.map((statusFilter) => (
                <ToggleButton
                  key={statusFilter.value}
                  value={statusFilter.value}
                >
                  {statusFilter.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Box flexGrow={1} />
            <Button
              onClick={() => {
                setApplyError(null);
                setConfirmOpen(true);
              }}
              isDisabled={refs.length === 0}
            >
              {`Apply approved (${refs.length})`}
            </Button>
          </Stack>
        </Component>

        <Component>
          {isLoading ? (
            <BigLoader />
          ) : isError ? (
            <Alert severity={"error"}>
              {"Failed to load staged config changes."}
            </Alert>
          ) : (
            <DataGrid
              rows={rows}
              columns={gridColumns}
              autoHeight
              rowHeight={52}
              disableRowSelectionOnClick
              onRowClick={(params) =>
                setDetailRef({
                  companyId: params.row.companyId,
                  id: params.row.record.id,
                })
              }
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              pageSizeOptions={[25, 50, 100]}
              slots={{ toolbar: GridToolbar }}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  printOptions: { disableToolbarButton: true },
                },
              }}
              localeText={{ noRowsLabel: "No staged config changes" }}
              sx={{
                border: "none",
                "& .MuiDataGrid-row": { cursor: "pointer" },
              }}
            />
          )}
        </Component>
      </DashboardContent>

      {detailRef && (
        <StagedChangeDetail
          open={true}
          onClose={() => setDetailRef(null)}
          companyId={detailRef.companyId}
          id={detailRef.id}
        />
      )}
      <ApplyConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleApply}
        onReview={(summary) =>
          setDetailRef({ companyId: summary.companyId, id: summary.id })
        }
        summaries={summaries}
        isApplying={isApplying}
        error={applyError}
      />
      <ApplyResultsDialog
        results={applyResults}
        onClose={() => setApplyResults(null)}
      />
    </AdminDashboard>
  );
};

export default ConfigStagingPage;
